/**
 * Data handler - calls Node.js server API which proxies to NetSuite RESTlet
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

/**
 * Fetch deal summary from the server with streaming (uses server-side AI processing with SSE)
 * @param {string} dealId - The HubSpot deal ID
 * @param {string} promptType - The prompt type (e.g., "sales", "implementation")
 * @param {object} callbacks - Callback functions for handling stream events
 * @param {function} callbacks.onDealData - Called when deal data is received
 * @param {function} callbacks.onSection - Called when a new section is received
 * @param {function} callbacks.onCached - Called when cached data is returned (complete response)
 * @param {function} callbacks.onDone - Called when streaming is complete
 * @param {function} callbacks.onError - Called on error
 * @returns {function} Abort function to cancel the stream
 */
export function fetchDealSummaryStream(dealId, promptType = "sales", callbacks = {}) {
    const startTime = Date.now();
    console.log(`[API] START: dealsummary stream (dealid=${dealId}, promptType=${promptType})`);

    const requestUrl = `${API_BASE_URL}/ns/dealsummary?dealid=${dealId}&promptType=${promptType}`;
    const eventSource = new EventSource(requestUrl, { withCredentials: true });

    eventSource.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);

            switch (data.type) {
                case "dealData":
                    callbacks.onDealData?.(data.dealData);
                    break;
                case "section":
                    callbacks.onSection?.(data.content);
                    break;
                case "cached":
                    callbacks.onCached?.(data);
                    console.log(
                        `[API] END: dealsummary stream - cached (${Date.now() - startTime}ms)`
                    );
                    break;
                case "done":
                    callbacks.onDone?.();
                    console.log(`[API] END: dealsummary stream (${Date.now() - startTime}ms)`);
                    eventSource.close();
                    break;
                case "error":
                    callbacks.onError?.(new Error(data.message));
                    console.error(
                        `[API] FAILED: dealsummary stream (${Date.now() - startTime}ms)`,
                        data.message
                    );
                    eventSource.close();
                    break;
                default:
                    console.warn("[API] Unknown event type:", data.type);
            }
        } catch (e) {
            console.error("[API] Error parsing SSE data:", e);
        }
    };

    eventSource.onerror = (error) => {
        console.error(`[API] FAILED: dealsummary stream (${Date.now() - startTime}ms)`, error);
        callbacks.onError?.(error);
        eventSource.close();
    };

    // Return abort function
    return () => {
        eventSource.close();
    };
}

/**
 * Fetch deal summary from the server (non-streaming, for backwards compatibility)
 * @param {string} dealId - The HubSpot deal ID
 * @param {string} promptType - The prompt type
 * @returns {Promise<{summary: string, dealData: object}>}
 */
export async function fetchDealSummary(dealId, promptType = "sales") {
    return new Promise((resolve, reject) => {
        let summary = "";
        let dealData = null;

        fetchDealSummaryStream(dealId, promptType, {
            onDealData: (data) => {
                dealData = data;
            },
            onSection: (content) => {
                summary += content;
            },
            onCached: (data) => {
                resolve({ summary: data.summary, dealData: data.dealData });
            },
            onDone: () => {
                resolve({ summary, dealData });
            },
            onError: (error) => {
                reject(error);
            },
        });
    });
}

const urlParams = new URLSearchParams(window.location.search);
let params = {};

for (const [key, value] of urlParams.entries()) {
    params[key] = value;
}

// Concurrency limiter - max 2 API calls in flight at a time
const MAX_CONCURRENT = 2;
let activeRequests = 0;
let pendingQueue = [];

const withConcurrencyLimit = (fn) => {
    return new Promise((resolve, reject) => {
        const execute = async () => {
            activeRequests++;
            try {
                const result = await fn();
                resolve(result);
            } catch (error) {
                reject(error);
            } finally {
                activeRequests--;
                // Process next pending request if any
                if (pendingQueue.length > 0) {
                    const next = pendingQueue.shift();
                    next();
                }
            }
        };

        if (activeRequests < MAX_CONCURRENT) {
            execute();
        } else {
            pendingQueue.push(execute);
        }
    });
};

// Store user permissions for error logging
let userPermissions = [];

export async function checkCustomerParam() {
    if (params) return params?.id;
}

/**
 * Fetch from the server and handle HTTP errors. The server (nsClient) already
 * retries transient/concurrency errors against NetSuite, so we deliberately do
 * NOT retry here — a retry on a 502 would re-run the whole backend retry chain
 * and multiply load against NetSuite's concurrency limit.
 */
async function fetchAndHandleErrors(requestUrl, fetchConfig, action) {
    const response = await fetch(requestUrl, fetchConfig);

    if (response.ok) {
        return response;
    }

    // Handle auth required - redirect to login
    if (response.status === 401) {
        const data = await response.json().catch(() => ({}));

        // Only redirect (full page reload) for a genuine MS/SSO authentication
        // failure. The SSO middleware is the only source that returns a
        // `loginUrl` (error "SSO login required"). Other 401s — e.g. a NetSuite
        // token error surfaced as code "AUTHENTICATION_ERROR", or a transient
        // backend blip during a restart — must NOT reload the page; surface them
        // as a thrown error the caller can handle inline.
        if (data.loginUrl) {
            console.log("[API] SSO session expired, redirecting to login...");
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `${data.loginUrl}?returnUrl=${returnUrl}`;
            throw new Error("Authentication required");
        }

        throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Attach the status and parsed server body so callers can branch the UI
    // (e.g. distinguish a NetSuite concurrency/"system busy" failure from a
    // generic error). For NetSuite errors the body carries
    // { code: "NETSUITE_ERROR", details: { error: { code: "SSS_REQUEST_LIMIT_EXCEEDED" } } }.
    const errorBody = await response.json().catch(() => ({}));

    // Notify the global error modal so any page using the data handler surfaces
    // a consistent message. A NetSuite concurrency rejection ("system busy with
    // many users") arrives as HTTP 429/503, or as a 502 whose body carries the
    // SuiteScript code SSS_REQUEST_LIMIT_EXCEEDED / REJECTEDACCOUNTCONCURRENCY.
    const nsCode = errorBody?.details?.error?.code;
    const isBusy =
        response.status === 429 ||
        response.status === 503 ||
        nsCode === "SSS_REQUEST_LIMIT_EXCEEDED" ||
        nsCode === "REJECTEDACCOUNTCONCURRENCY";
    window.dispatchEvent(
        new CustomEvent("apiError", {
            detail: {
                status: response.status,
                isBusy,
                action,
                code: errorBody?.code || nsCode,
                message: errorBody?.error,
                time: new Date().toISOString(),
            },
        })
    );

    const error = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    error.body = errorBody;
    throw error;
}

/**
 * Fetch data from the server API
 * @param {string} action - The action/endpoint to call (e.g., 'listcustomers', 'getmetrics')
 * @param {object} options - Query parameters
 * @param {string} method - HTTP method (GET or POST)
 * @param {object} postData - Data to send in POST body
 */
export function fetchData(action, options = null, method = "GET", postData = null) {
    return withConcurrencyLimit(async () => {
        const startTime = Date.now();
        console.log(`[API] START: ${action}`);

        const queryParams = new URLSearchParams({ action, ...options });
        const requestUrl = `${API_BASE_URL}/ns/data?${queryParams.toString()}`;

        const fetchConfig = {
            method,
            credentials: "include", // Include cookies for session auth
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        };

        if (method === "POST" && postData) {
            fetchConfig.body = JSON.stringify(postData);
        }

        try {
            const response = await fetchAndHandleErrors(requestUrl, fetchConfig, action);
            const data = await response.json();

            // Store user permissions when access action is called
            if (action === "access" && Array.isArray(data)) {
                userPermissions = data;
            }

            // Log permission denied errors with user's permissions
            if (data.error === "Access denied") {
                console.error(
                    `[API] ACCESS DENIED: ${action} - User lacks required permission. User permissions:`,
                    userPermissions
                );
            }

            console.log(`[API] END: ${action} (${Date.now() - startTime}ms)`);
            return data;
        } catch (error) {
            console.error(`[API] FAILED: ${action} (${Date.now() - startTime}ms)`, error);
            throw error;
        }
    });
}

/**
 * Fetch server configuration
 * @returns {Promise<{isDevRestlet: boolean}>}
 */
export async function fetchServerConfig() {
    const response = await fetch(`${API_BASE_URL}/ns/config`, { credentials: "include" });
    return response.json();
}
